import Enumerable from "linq";
import { IPropertyEditable, IPropertyPresentable } from "../../../../base/domain/system/edit";
import { TextAlignKinds } from "../../../../base/domain/drawing";
import { HisInformationSetting } from "../../../../base/domain/his";
import {
  IHistoryRecord,
  IPrintBehavior,
  IEditBehavior,
  IQuoteBehavior,
  IFreeInputBehavior,
  INaturalTextBehavior,
  ITextSearchBehavior,
  IGroupAlertBehavior,
  IEmptyAlertBehavior,
  ILengthAlertBehavior,
  ITooltipBehavior,
  IItemGroupSelectBehavior,
  IHisInformationItemDefaultSelectBehavior,
  HisInformationItemDefaultSelectKinds,
  IHospitalizedHisInformationItemSelectBehavior,
  IUserDefinedInformationKey,
  IUserDefinedItemSelectBehavior,
  DateObjectKinds,
  IDateStyle,
  ITimeStyle,
  IDateInputBehavior,
  ITimeInputBehavior,
  IDateCalculateBehavior,
  IObjectNameIdentifier,
  ILinkedObjectSpecifiable,
  IObjectsControllable,
  ILinkedPageObjectSpecifiable,
  IObjectControllableFeature,
  ILinkedObjectGroupSpecifiable,
  TranscribeTargetKinds,
} from "../../../../documaker/domain/format/objects";
import { IFormatObjectEditable } from "../IFormatObjectEditable";
import { IFormatSpecification, IUserDefinitionSpecification } from "../IFormatSpecification";
import { IDateObject } from "../objects";
import {
  IInformationKeyHistoriesPropertyEditable,
  IPrintBehaviorPropertyEditable,
  IEditBehaviorPropertyEditable,
  IQuoteBehaviorPropertyEditable,
  IFreeInputBehaviorPropertyEditable,
  INaturalTextBehaviorPropertyEditable,
  ITextSearchBehaviorPropertyEditable,
  IGroupAlertBehaviorPropertyEditable,
  IEmptyAlertBehaviorPropertyEditable,
  ILengthAlertBehaviorPropertyEditable,
  ITooltipBehaviorPropertyEditable,
  IItemGroupSelectBehaviorPropertyEditable,
  IHisInformationItemDefaultSelectBehaviorPropertyEditable,
  IHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
  IUserDefinedInformationKeyPropertyEditable,
  IUserDefinedItemSelectBehaviorPropertyEditable,
  IDataCalculateBehaviorPropertyEditable,
  ITranscribeBehaviorPropertyEditable,
} from "../properties";
import { AbstractDateTextDrawableObjectEditor } from "./AbstractDateTextDrawableObjectEditor";

export class DateObjectEditor extends AbstractDateTextDrawableObjectEditor<IDateObject> implements
IInformationKeyHistoriesPropertyEditable,
IPrintBehaviorPropertyEditable,
IEditBehaviorPropertyEditable,
IQuoteBehaviorPropertyEditable,
IFreeInputBehaviorPropertyEditable,
INaturalTextBehaviorPropertyEditable,
ITextSearchBehaviorPropertyEditable,
IGroupAlertBehaviorPropertyEditable,
IEmptyAlertBehaviorPropertyEditable,
ILengthAlertBehaviorPropertyEditable,
ITooltipBehaviorPropertyEditable,
IItemGroupSelectBehaviorPropertyEditable,
IHisInformationItemDefaultSelectBehaviorPropertyEditable,
IHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
IUserDefinedInformationKeyPropertyEditable,
IUserDefinedItemSelectBehaviorPropertyEditable,
IDataCalculateBehaviorPropertyEditable,
ITranscribeBehaviorPropertyEditable {
  constructor(source: IDateObject | DateObjectEditor, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._id = this.createIdPropertyEditor();
    // nameはv1の@title/@name-sub(v1の表示名はサブ名称)として扱うので常に有効.
    this._name = this.createNamePropertyEditor();
    // nameHistoriesはHIS情報等情報を示す場合は情報キーの方の履歴を記録する.(更新する情報は一緒.UIが別物なので分ける.)
    this._nameHistories = this.createNameHistoriesPropertyEditor(s => !this.isInformationSpecifiable(s));
    this._informationKeyHistories = this.createNameHistoriesPropertyEditor(s => this.isInformationSpecifiable(s));
    this._hisInformationKey = this.createInformationKeyPropertyEditor(s => s.objectKind === DateObjectKinds.HisItem);
    this._userDefinedInformationKey = this.createPropertyEditor(
      s => ({
        name: s.userDefinedInformationSource.fileName,
        column: s.informationKey,
      }),
      (s, v) => {
        s.userDefinedInformationSource.fileName = v.name;
        s.informationKey = v.column;
      },
      s => (s.objectKind === DateObjectKinds.UserDefined));
    this._bookmark = this.createBookmarkPropertyEditor();
    this._printBehavior = this.createPropertyEditor(s => s.printBehavior, (s, v) => s.printBehavior = v);
    this._editBehavior = this.createPropertyEditor(s => s.editBehavior, (s, v) => s.editBehavior = v);
    this._quoteBehavior = this.createPropertyEditor(s => s.quoteBehavior, (s, v) => s.quoteBehavior = v);
    this._freeInputBehavior = this.createPropertyEditor(s => s.freeInputBehavior, (s, v) => s.freeInputBehavior = v);
    this._naturalTextBehavior = this.createPropertyEditor(s => s.naturalTextBehavior, (s, v) => s.naturalTextBehavior = v);
    this._textSearchBehavior = this.createPropertyEditor(s => s.textSearchBehavior, (s, v) => s.textSearchBehavior = v);
    this._groupAlertBehavior = this.createPropertyEditor(s => s.groupAlertBehavior, (s, v) => s.groupAlertBehavior = v);
    this._emptyAlertBehavior = this.createPropertyEditor(s => s.emptyAlertBehavior, (s, v) => s.emptyAlertBehavior = v);
    this._lengthAlertBehavior = this.createPropertyEditor(s => s.lengthAlertBehavior, (s, v) => s.lengthAlertBehavior = v);
    this._tooltipBehavior = this.createPropertyEditor(s => s.tooltipBehavior, (s, v) => s.tooltipBehavior = v);
    this._itemGroupSelectBehavior = this.createPropertyEditor(
      s => s.itemGroupSelectBehavior,
      (s, v) => s.itemGroupSelectBehavior = v,
      s => (s.objectKind === DateObjectKinds.HisItem) || (s.objectKind === DateObjectKinds.UserDefined));
    this._hisInformationItemDefaultSelectBehavior = this.createPropertyEditor(
      s => s.hisInformationItemDefaultSelectBehavior,
      (s, v) => s.hisInformationItemDefaultSelectBehavior = v,
      s => (s.objectKind === DateObjectKinds.HisItem)); // @initialはv1だとHIS項目以外でも設定できるが見てないらしい.設定できないようにする.
    this._hospitalizedHisInformationItemSelectBehavior = this.createPropertyEditor(
      s => s.hospitalizedHisInformationItemSelectBehavior,
      (s, v) => s.hospitalizedHisInformationItemSelectBehavior = v,
      s => {
        const selectedHisInfo = this.getSelectedHisInformation();
        return (s.objectKind === DateObjectKinds.HisItem) && !!selectedHisInfo && (selectedHisInfo.itemType === "HOSPITALIZATION");
      });
    this._userDefinedItemSelectBehavior = this.createPropertyEditor(
      s => s.userDefinedItemSelectBehavior,
      (s, v) => s.userDefinedItemSelectBehavior = v,
      s => (s.objectKind === DateObjectKinds.UserDefined));
    this._objectKind = this.createPropertyPresenter(s => s.objectKind);
    this._isDate = this.createPropertyPresenter(s => s.isDate);
    this._dateStyle = this.createPropertyEditor(s => s.style as IDateStyle, (s, v) => s.style = v, s => s.isDate);
    this._timeStyle = this.createPropertyEditor(s => s.style as ITimeStyle, (s, v) => s.style = v, s => !s.isDate);
    this._dateInputBehavior = this.createPropertyEditor(s => s.inputBehavior as IDateInputBehavior, (s, v) => s.inputBehavior = v, s => s.isDate);
    this._timeInputBehavior = this.createPropertyEditor(s => s.inputBehavior as ITimeInputBehavior, (s, v) => s.inputBehavior = v, s => !s.isDate);
    this._dateCalculateBehavior = this.createDateCalculateBehaviorPropertyEditor(() => (this._objectKind.getValue() === DateObjectKinds.Normal && this._isDate.getValue()));
    this._scriptExecuteTriggerBehavior = this.createPropertyEditor(s => s.scriptExecuteTriggerBehavior, (s, v) => s.scriptExecuteTriggerBehavior = v);
    this._transcribeBehavior = this.createPropertyEditor(s => s.transcribeBehavior, (s, v) => s.transcribeBehavior = v);
    this._emptyAlertAllowBehavior = this.createPropertyEditor(s => s.emptyAlertAllowBehavior, (s, v) => s.emptyAlertAllowBehavior = v);
    this._transcribeDateOfTimeBehavior = this.createPropertyEditor(
      s => s.transcribeDateOfTimeBehavior,
      (s, v) => s.transcribeDateOfTimeBehavior = v,
      s => (!s.isDate) && (s.objectKind === DateObjectKinds.Normal));
    this._groupAlertAllowBehavior = this.createPropertyEditor(s => s.groupAlertAllowBehavior, (s, v) => s.groupAlertAllowBehavior = v);
  }

  getSelectableHisInformations(): HisInformationSetting[] {
    const type = this.isDate().getValue() ? "DATE" : "TIME";
    return Enumerable.from(this._specification.objects.date.hisInformations)
      .where(s => s.objectType === type)
      .toArray();
  }

  getSelectableUserDefinedInformations(): IUserDefinitionSpecification[] {
    return this._specification.userDefinitions;
  }

  protected _informationKeyHistories: IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]>;
  protected _printBehavior: IPropertyEditable<IPrintBehavior>;
  protected _editBehavior: IPropertyEditable<IEditBehavior>;
  protected _quoteBehavior: IPropertyEditable<IQuoteBehavior>;
  protected _freeInputBehavior: IPropertyEditable<IFreeInputBehavior>;
  protected _naturalTextBehavior: IPropertyEditable<INaturalTextBehavior>;
  protected _textSearchBehavior: IPropertyEditable<ITextSearchBehavior>;
  protected _groupAlertBehavior: IPropertyEditable<IGroupAlertBehavior>;
  protected _emptyAlertBehavior: IPropertyEditable<IEmptyAlertBehavior>;
  protected _lengthAlertBehavior: IPropertyEditable<ILengthAlertBehavior>;
  protected _tooltipBehavior: IPropertyEditable<ITooltipBehavior>;
  protected _itemGroupSelectBehavior: IPropertyEditable<IItemGroupSelectBehavior>;
  protected _hisInformationItemDefaultSelectBehavior: IPropertyEditable<IHisInformationItemDefaultSelectBehavior>;
  protected _hospitalizedHisInformationItemSelectBehavior: IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior>;
  protected _userDefinedInformationKey: IPropertyEditable<IUserDefinedInformationKey>;
  protected _userDefinedItemSelectBehavior: IPropertyEditable<IUserDefinedItemSelectBehavior>;
  protected _dateCalculateBehavior: IPropertyEditable<IDateCalculateBehavior>;
  protected _objectKind: IPropertyPresentable<DateObjectKinds>;
  protected _isDate: IPropertyPresentable<boolean>;
  protected _dateStyle: IPropertyEditable<IDateStyle>;
  protected _timeStyle: IPropertyEditable<ITimeStyle>;
  protected _dateInputBehavior: IPropertyEditable<IDateInputBehavior>;
  protected _timeInputBehavior: IPropertyEditable<ITimeInputBehavior>;
  protected _scriptExecuteTriggerBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _transcribeBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _emptyAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _transcribeDateOfTimeBehavior: IPropertyEditable<IObjectControllableFeature<ILinkedPageObjectSpecifiable>>;
  protected _groupAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>>;

  protected createDateCalculateBehaviorPropertyEditor(isWritable?: (s: IDateObject) => boolean): IPropertyEditable<IDateCalculateBehavior> {
    return this.createPropertyEditor(
      s => s.dateCalculateBehavior,
      (s, v) => s.dateCalculateBehavior = v,
      isWritable ? isWritable : s => true);
  }

  informationKeyHistories(): IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]> {
    return this._informationKeyHistories;
  }

  userDefinedInformationKey(): IPropertyEditable<IUserDefinedInformationKey> {
    return this._userDefinedInformationKey;
  }

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  editBehavior(): IPropertyEditable<IEditBehavior> {
    return this._editBehavior;
  }

  quoteBehavior(): IPropertyEditable<IQuoteBehavior> {
    return this._quoteBehavior;
  }

  freeInputBehavior(): IPropertyEditable<IFreeInputBehavior> {
    return this._freeInputBehavior;
  }

  naturalTextBehavior(): IPropertyEditable<INaturalTextBehavior> {
    return this._naturalTextBehavior;
  }

  textSearchBehavior(): IPropertyEditable<ITextSearchBehavior> {
    return this._textSearchBehavior;
  }

  groupAlertBehavior(): IPropertyEditable<IGroupAlertBehavior> {
    return this._groupAlertBehavior;
  }

  emptyAlertBehavior(): IPropertyEditable<IEmptyAlertBehavior> {
    return this._emptyAlertBehavior;
  }

  lengthAlertBehavior(): IPropertyEditable<ILengthAlertBehavior> {
    return this._lengthAlertBehavior;
  }

  tooltipBehavior(): IPropertyEditable<ITooltipBehavior> {
    return this._tooltipBehavior;
  }

  itemGroupSelectBehavior(): IPropertyEditable<IItemGroupSelectBehavior> {
    return this._itemGroupSelectBehavior;
  }

  hisInformationItemDefaultSelectBehavior(): IPropertyEditable<IHisInformationItemDefaultSelectBehavior> {
    return this._hisInformationItemDefaultSelectBehavior;
  }

  getSelectableHisInformationItemDefaultSelectKinds(): HisInformationItemDefaultSelectKinds[] {
    return this.isDate().getValue()
      ? this._specification.objects.date.kinds.date.hisInformationItemDefaultSelectKinds
      : this._specification.objects.date.kinds.time.hisInformationItemDefaultSelectKinds;
  }

  hospitalizedHisInformationItemSelectBehavior(): IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior> {
    return this._hospitalizedHisInformationItemSelectBehavior;
  }

  userDefinedItemSelectBehavior(): IPropertyEditable<IUserDefinedItemSelectBehavior> {
    return this._userDefinedItemSelectBehavior;
  }

  dateCalculateBehavior(): IPropertyEditable<IDateCalculateBehavior> {
    return this._dateCalculateBehavior;
  }

  objectKind(): IPropertyPresentable<DateObjectKinds> {
    return this._objectKind;
  }

  private isInformationSpecifiable(s: IDateObject): boolean {
    return (s.objectKind === DateObjectKinds.HisItem)
        || (s.objectKind === DateObjectKinds.UserDefined);
  }

  isDate(): IPropertyPresentable<boolean> {
    return this._isDate;
  }

  dateStyle(): IPropertyEditable<IDateStyle> {
    return this._dateStyle;
  }

  timeStyle(): IPropertyEditable<ITimeStyle> {
    return this._timeStyle;
  }

  dateInputBehavior(): IPropertyEditable<IDateInputBehavior> {
    return this._dateInputBehavior;
  }

  timeInputBehavior(): IPropertyEditable<ITimeInputBehavior> {
    return this._timeInputBehavior;
  }

  scriptExecuteTriggerBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._scriptExecuteTriggerBehavior;
  }

  transcribeBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._transcribeBehavior;
  }

  getSelectableTranscribeTargets(): TranscribeTargetKinds[] {
    return this._specification.objects.date.transcription.targets;
  }

  emptyAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._emptyAlertAllowBehavior;
  }

  transcribeDateOfTimeBehavior(): IPropertyEditable<IObjectControllableFeature<ILinkedPageObjectSpecifiable>> {
    return this._transcribeDateOfTimeBehavior;
  }

  groupAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>> {
    return this._groupAlertAllowBehavior;
  }

  getTypeName(): string {
    const objectKind = this._objectKind.getValue();
    const typeName = this._isDate.getValue() ? "日付" : "時刻";
    switch (objectKind) {
      case DateObjectKinds.HisItem:
        return "HIS項目" + typeName;
      case DateObjectKinds.UserDefined:
        return "ユーザ定義" + typeName;
      default:
        return typeName;
    }
  }

  getIdentifiableName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case DateObjectKinds.HisItem:
        return this.getIdentifiableHisName();
      default:
        return this.name().getValue();
    }
  }

  toLinkedObjectIdentifier(): ILinkedObjectSpecifiable {
    return {
      type: this.objectTypeName().getValue(),
      id: this.id().getValue(),
      key: this.getLinkedObjectKey(),
      isDate: this.isDate().getValue(),
    };
  }

  private getLinkedObjectKey(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case DateObjectKinds.HisItem:
        return this.hisInformationKey().getValue();
      case DateObjectKinds.UserDefined:
        return this.userDefinedInformationKey().getValue().column;
      default:
        return this.name().getValue();
    }
  }

  getSelectableAlignmentKinds(): TextAlignKinds[] {
    return this._specification.objects.date.alignmentKinds;
  }

  getWarningName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case DateObjectKinds.HisItem:
        return this.getHisInformationName();
      case DateObjectKinds.Normal:
        return this.name().getValue();
      default: return "";
    }
  }

  getWarningSettingName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case DateObjectKinds.HisItem:
        return this.hisInformationKey().getValue();
      case DateObjectKinds.Normal:
        return this.name().getValue();
      default: return "";
    }
  }

  isSettableWarningName(): boolean {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case DateObjectKinds.HisItem:
      case DateObjectKinds.Normal:
        return true;
      default: return false;
    }
  }

  clone(): IFormatObjectEditable {
    return new DateObjectEditor(this);
  }
}
